import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { register as apiRegister } from '../services/api';

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  
  // Simple logout on component mount to ensure clean state
  useEffect(() => {
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, [logout]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      setValidationError('Password must be at least 6 characters long');
      return false;
    }
    if (!agreedToTerms) {
      setValidationError('You must agree to the Terms of Service and Privacy Policy to register.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      };
      
      const response = await apiRegister(userData);
      login(response.data.user, response.data.token);
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Sign Up - Pelican Beaks</title>
        <meta name="description" content="Create your free Pelican Beaks account to start generating AI travel itineraries." />
      </Helmet>

      {/* Copied styles from HomePage.js */}
      <style>
        {`
          @keyframes float {
            0% {
              transform: translate(0, 0) scale(1);
            }
            50% {
              transform: translate(2%, 2%) scale(1.02);
            }
            100% {
              transform: translate(-2%, -2%) scale(0.98);
            }
          }

          .background-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            overflow: hidden;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          .gradient-blob {
            position: absolute;
            border-radius: 100%;
            filter: blur(60px);
            opacity: 0.6;
            animation: float 20s infinite alternate ease-in-out;
          }

          .gradient-blob:nth-child(1) {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            width: 150vw;
            height: 150vw;
            top: -100%;
            left: -25%;
            animation-delay: 0s;
          }

          .gradient-blob:nth-child(2) {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            width: 150vw;
            height: 150vw;
            bottom: -100%;
            right: -25%;
            animation-delay: -5s;
          }

          .gradient-blob:nth-child(3) {
            background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
            width: 100vw;
            height: 100vw;
            top: 30%;
            left: -50%;
            animation-delay: -10s;
          }

          @media (min-width: 640px) {
            .gradient-blob {
              filter: blur(50px);
              opacity: 0.35;
            }
          }

          @media (min-width: 768px) {
            .gradient-blob {
              filter: blur(60px);
              opacity: 0.4;
            }

            .gradient-blob:nth-child(1) {
              width: 120vw;
              height: 120vw;
              top: -70%;
              left: -20%;
            }

            .gradient-blob:nth-child(2) {
              width: 120vw;
              height: 120vw;
              bottom: -70%;
              right: -20%;
            }

            .gradient-blob:nth-child(3) {
              width: 80vw;
              height: 80vw;
              top: 30%;
              left: -30%;
            }
          }

          @media (min-width: 1024px) {
            .gradient-blob {
              filter: blur(80px);
              opacity: 0.5;
            }

            .gradient-blob:nth-child(1) {
              width: 60vw;
              height: 60vw;
              top: -20%;
              left: -10%;
            }

            .gradient-blob:nth-child(2) {
              width: 60vw;
              height: 60vw;
              bottom: -30%;
              right: -10%;
            }

            .gradient-blob:nth-child(3) {
              width: 40vw;
              height: 40vw;
              top: 40%;
              left: 30%;
            }
          }
        `}
      </style>

      {/* Animated Gradient Background */}
      <div className="background-container">
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
      </div>

    <Container component="main" maxWidth="xs">
      <Box
        sx={{
            height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
            justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(12px)',
              borderRadius: 16
          }}
        >
          <Typography component="h1" variant="h5">
            Sign up
          </Typography>
          {(error || validationError) && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error || validationError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <FormControlLabel
              sx={{ mt: 1, width: '100%', alignItems: 'flex-start' }}
              control={
                <Checkbox
                  value="agree"
                  color="primary"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{ mt: -0.5 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  I agree to the{' '}
                  <Link component={RouterLink} to="/terms" underline="hover">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link component={RouterLink} to="/privacy" underline="hover">
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing up...' : 'Sign Up'}
            </Button>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {"Already have an account? Sign In"}
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/" variant="body2">
                {"Back to Home Page"}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
    </Box>
  );
}

export default Register;